// ============================================================================
// MEAL PLAN — MAIN MEAL
// ============================================================================
// The meal plan screen edits lunch and dinner as one "Main Meal" choice. It is a
// view over the stored columns (includes_lunch, includes_dinner,
// default_major_meal); nothing new is stored. A plan whose main meal is "one of
// lunch or dinner" (MAP) stores both meals plus the default meal.

export type MainMeal = 'NONE' | 'LUNCH' | 'DINNER' | 'ONE_OF' | 'BOTH';

export const MAIN_MEAL_OPTIONS: { value: MainMeal; label: string }[] = [
  { value: 'NONE', label: 'None' },
  { value: 'LUNCH', label: 'Lunch only' },
  { value: 'DINNER', label: 'Dinner only' },
  { value: 'ONE_OF', label: 'One of lunch or dinner (MAP)' },
  { value: 'BOTH', label: 'Both lunch and dinner (AP)' },
];

interface StoredMeals {
  includesLunch: boolean;
  includesDinner: boolean;
  defaultMajorMeal?: string | null;
}

export function toMainMeal(m: StoredMeals): MainMeal {
  if (m.includesLunch && m.includesDinner) return m.defaultMajorMeal ? 'ONE_OF' : 'BOTH';
  if (m.includesLunch) return 'LUNCH';
  if (m.includesDinner) return 'DINNER';
  return 'NONE';
}

/** Stored columns for a main meal; the default meal is kept only for ONE_OF. */
export function fromMainMeal(mainMeal: MainMeal, defaultMainMeal: string): Required<StoredMeals> {
  return {
    includesLunch: mainMeal === 'LUNCH' || mainMeal === 'ONE_OF' || mainMeal === 'BOTH',
    includesDinner: mainMeal === 'DINNER' || mainMeal === 'ONE_OF' || mainMeal === 'BOTH',
    defaultMajorMeal: mainMeal === 'ONE_OF' && defaultMainMeal ? defaultMainMeal : null,
  };
}

const mealName = (meal?: string | null) => (meal === 'LUNCH' ? 'Lunch' : 'Dinner');

/** "Breakfast + Lunch or Dinner (default Dinner)", "Breakfast + Lunch", "Room only". */
export function describeMeals(m: StoredMeals & { includesBreakfast: boolean }): string {
  const parts: string[] = [];
  if (m.includesBreakfast) parts.push('Breakfast');
  switch (toMainMeal(m)) {
    case 'LUNCH': parts.push('Lunch'); break;
    case 'DINNER': parts.push('Dinner'); break;
    case 'BOTH': parts.push('Lunch', 'Dinner'); break;
    case 'ONE_OF': parts.push(`Lunch or Dinner (default ${mealName(m.defaultMajorMeal)})`); break;
  }
  return parts.length ? parts.join(' + ') : 'No meals';
}
